/**
 * Per-driver footage downloads for one stage. Rows come from the same
 * stage-sessions endpoint as the Sessions card — the two must never
 * diverge in scope.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import useStageSessions from '@/hooks/useStageSessions';
import {
  EnrollmentRow,
  FleetApiError,
  downloadStageFootage,
  listEnrollments,
} from '@/services/fleet';

interface DriverFootage {
  deviceId: string;
  driver: string | null;
  vehicle: string | null;
  carNumber: string | null;
  sessionCount: number;
}

const StageFootageCard: React.FC<{
  eventId: number;
  stageIndex: number;
}> = ({ eventId, stageIndex }) => {
  const { sessions } = useStageSessions(eventId, stageIndex);
  const [enrollments, setEnrollments] = useState<EnrollmentRow[]>([]);
  // device_id of the in-flight download, '*' for the whole stage.
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listEnrollments(eventId)
      .then((rows) => {
        if (!cancelled) setEnrollments(rows);
      })
      .catch(() => {
        // Labels are cosmetic; rows fall back to device ids.
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const drivers = useMemo<DriverFootage[]>(() => {
    const byDevice = new Map<string, number>();
    for (const s of sessions) {
      byDevice.set(s.device_id, (byDevice.get(s.device_id) ?? 0) + 1);
    }
    const label = new Map(enrollments.map((e) => [e.device_id, e]));
    return [...byDevice.entries()]
      .map(([deviceId, sessionCount]) => {
        const e = label.get(deviceId);
        return {
          deviceId,
          driver: e?.driver_name ?? null,
          vehicle: e?.vehicle ?? null,
          carNumber: e?.car_number ?? null,
          sessionCount,
        };
      })
      .sort((a, b) => (a.driver ?? a.deviceId).localeCompare(b.driver ?? b.deviceId));
  }, [sessions, enrollments]);

  const download = async (deviceId?: string) => {
    setBusy(deviceId ?? '*');
    setError(null);
    try {
      await downloadStageFootage(eventId, stageIndex, deviceId);
    } catch (err) {
      setError(
        err instanceof FleetApiError && typeof err.detail === 'string'
          ? err.detail
          : err instanceof Error
            ? err.message
            : 'download failed',
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="p-4">
      <CardContent className="p-0">
        <h3 className="mb-2 flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wider text-fg-secondary">
          <span>Footage</span>
          {drivers.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1.5 text-[11px] normal-case tracking-normal"
              disabled={busy != null}
              onClick={() => void download()}
            >
              {busy === '*' ? <Spinner className="size-3" /> : <Download className="size-3" />}
              Download all
            </Button>
          )}
        </h3>
        {error && <p className="mb-2 text-xs text-status-error">{error}</p>}
        {drivers.length === 0 ? (
          <p className="text-xs text-fg-muted">No footage on this stage yet.</p>
        ) : (
          <>
            <ul className="divide-y divide-border text-xs">
              {drivers.map((d) => (
                <li key={d.deviceId} className="flex items-center gap-3 py-1.5">
                  {d.carNumber && (
                    <span className="shrink-0 rounded bg-bg-input px-1.5 py-0.5 font-mono text-[11px] font-semibold text-fg">
                      #{d.carNumber}
                    </span>
                  )}
                  <span className="min-w-0 flex-1 truncate text-fg" title={d.deviceId}>
                    {d.driver ?? d.deviceId}
                    {d.vehicle && <span className="text-fg-muted"> · {d.vehicle}</span>}
                  </span>
                  <span className="shrink-0 font-mono text-fg-secondary">
                    {d.sessionCount} session{d.sessionCount === 1 ? '' : 's'}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 shrink-0 gap-1.5 text-[11px]"
                    disabled={busy != null}
                    onClick={() => void download(d.deviceId)}
                  >
                    {busy === d.deviceId ? (
                      <Spinner className="size-3" />
                    ) : (
                      <Download className="size-3" />
                    )}
                    Download
                  </Button>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] text-fg-muted">
              One MP4 per camera per session, zipped. The first download of a
              stage prepares the files (minutes); repeats start right away.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StageFootageCard;
